'use client';

import { cn } from '@/lib/utils';

interface HourlyHeatmapProps {
  todayHourlyLoad: { hour: number; customers: number; revenue: number }[];
  weeklyHeatmap?: { day: number; hour: number; avgCustomers: number }[][];
  showRevenue?: boolean;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Get color intensity based on value (0-1 scale) - grayscale only
function getIntensityColor(intensity: number, isToday: boolean = false): string {
  if (intensity === 0) {
    return 'bg-muted/20';
  }

  // Use grayscale for all data
  if (intensity < 0.25) return 'bg-foreground/10';
  if (intensity < 0.5) return 'bg-foreground/20';
  if (intensity < 0.75) return 'bg-foreground/30';
  return 'bg-foreground/40';
}

function formatHour(hour: number): string {
  if (hour === 0) return '12am';
  if (hour === 12) return '12pm';
  if (hour < 12) return `${hour}am`;
  return `${hour - 12}pm`;
}

export function HourlyHeatmap({
  todayHourlyLoad,
  weeklyHeatmap,
  showRevenue = false,
}: HourlyHeatmapProps) {
  // Find max values for normalization
  const maxCustomers = Math.max(...todayHourlyLoad.map((h) => h.customers), 1);
  const maxRevenue = Math.max(...todayHourlyLoad.map((h) => h.revenue), 1);

  // Calculate totals
  const totalCustomers = todayHourlyLoad.reduce((sum, h) => sum + h.customers, 0);
  const totalRevenue = todayHourlyLoad.reduce((sum, h) => sum + h.revenue, 0);

  // Find peak hour
  const peakHour = todayHourlyLoad.reduce(
    (peak, h) => (h.customers > peak.customers ? h : peak),
    todayHourlyLoad[0] || { hour: 0, customers: 0, revenue: 0 }
  );

  return (
    <div className="card-bookdesk !p-0 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
        <h3 className="text-base font-semibold text-foreground">Hourly Load</h3>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>
            Total: <span className="font-semibold text-foreground">{totalCustomers}</span> customers
          </span>
          {peakHour.customers > 0 && (
            <span>
              Peak: <span className="font-semibold text-foreground">{formatHour(peakHour.hour)}</span>{' '}
              ({peakHour.customers})
            </span>
          )}
        </div>
      </div>

      {/* Today's Hourly Grid */}
      <div className="p-4">
        <div className="mb-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Today
          </span>
        </div>

        <div className="flex gap-1">
          {todayHourlyLoad.map((hourData) => {
            const intensity = showRevenue
              ? hourData.revenue / maxRevenue
              : hourData.customers / maxCustomers;

            return (
              <div
                key={hourData.hour}
                className="flex-1 flex flex-col items-center gap-1"
              >
                <div
                  className={cn(
                    'w-full aspect-square rounded-md flex items-center justify-center transition-colors',
                    getIntensityColor(intensity, true)
                  )}
                  title={`${formatHour(hourData.hour)}: ${hourData.customers} customers, AED ${hourData.revenue.toFixed(0)}`}
                >
                  {hourData.customers > 0 && (
                    <span className="text-[10px] font-medium text-foreground/70">
                      {hourData.customers}
                    </span>
                  )}
                </div>
                <span className="text-[9px] text-muted-foreground">
                  {hourData.hour % 3 === 0 ? formatHour(hourData.hour) : ''}
                </span>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground">Low</span>
            <div className="flex gap-0.5">
              {[0.1, 0.35, 0.6, 0.85].map((intensity) => (
                <div
                  key={intensity}
                  className={cn('w-3 h-3 rounded-sm', getIntensityColor(intensity))}
                />
              ))}
            </div>
            <span className="text-[10px] text-muted-foreground">High</span>
          </div>

          {showRevenue && totalRevenue > 0 && (
            <span className="text-xs text-muted-foreground">
              Revenue: AED {totalRevenue.toFixed(0)}
            </span>
          )}
        </div>
      </div>

      {/* Weekly Heatmap (if provided) */}
      {weeklyHeatmap && weeklyHeatmap.length > 0 && (
        <div className="border-t border-border p-4">
          <div className="mb-3">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Weekly Pattern
            </span>
          </div>

          <div className="space-y-1">
            {/* Hour labels */}
            <div className="flex gap-0.5 ml-10">
              {todayHourlyLoad.map((h) => (
                <div key={h.hour} className="flex-1 text-center">
                  {h.hour % 3 === 0 && (
                    <span className="text-[8px] text-muted-foreground">
                      {formatHour(h.hour)}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Day rows */}
            {DAYS.map((day, dayIndex) => {
              const dayData = weeklyHeatmap[dayIndex] || [];
              const maxForWeek = Math.max(
                ...weeklyHeatmap.flat().map((h) => h?.avgCustomers || 0),
                1
              );

              return (
                <div key={day} className="flex items-center gap-1">
                  <span className="text-[9px] text-muted-foreground w-8">{day}</span>
                  <div className="flex gap-0.5 flex-1">
                    {todayHourlyLoad.map((_, hourIndex) => {
                      const cellData = dayData.find((d) => d?.hour === hourIndex);
                      const intensity = cellData
                        ? cellData.avgCustomers / maxForWeek
                        : 0;

                      return (
                        <div
                          key={hourIndex}
                          className={cn(
                            'flex-1 h-3 rounded-sm',
                            getIntensityColor(intensity, false)
                          )}
                          title={`${day} ${formatHour(hourIndex)}: ${cellData?.avgCustomers || 0} avg`}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
